'use client'
import React, { useState } from 'react'
import { discardHTMLTags } from '@/constants/hooks'
import { ChevronDown, ChevronUp, Award } from 'lucide-react'

interface Props {
    details: string
    title?: string
    id?: string
    defaultExpanded?: boolean
    showButton?: boolean
}

const MerchantDetailsShort = ({ details, title, id, defaultExpanded = false, showButton = true }: Props) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded)
    const cleanDetails = discardHTMLTags(details || '').replace(/&nbsp;/gi, ' ').trim()
    const isLong = cleanDetails.length > 200

    if (!details || !cleanDetails || cleanDetails.length === 0) return null

    return (
        <div id={id} className="bg-white rounded-3xl border border-slate-200/80 overflow-hidden shadow-xs transition-all duration-300">
            {/* Header */}
            {title && (
                <div className="bg-slate-50/80 px-6 py-4 border-b border-slate-200/80 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <Award size={18} className="text-[#00a87a]" />
                        <h3 className="text-sm font-bold text-slate-900">
                            {title}
                        </h3>
                    </div>
                </div>
            )}

            {/* Content Body */}
            <div className="p-6 sm:p-7">
                {!isExpanded && isLong ? (
                    <div className="space-y-4">
                        <p className="text-sm text-slate-800 leading-relaxed line-clamp-3">
                            {cleanDetails}
                        </p>
                        {showButton && (
                            <button
                                type="button"
                                onClick={() => setIsExpanded(true)}
                                className="group inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-emerald-50 hover:bg-emerald-100 text-[#00a87a] border border-emerald-200 transition-all duration-300 cursor-pointer shadow-xs"
                            >
                                <span>Read More</span>
                                <ChevronDown size={14} className="text-emerald-700 group-hover:translate-y-0.5 transition-transform" />
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div
                            className="theme12-merchant-details text-sm text-slate-800 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: details }}
                        />
                        {isLong && showButton && isExpanded && (
                            <div className="pt-3 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setIsExpanded(false)}
                                    className="group inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-[#071822] hover:bg-[#071822] text-white transition-all duration-300 cursor-pointer shadow-xs"
                                >
                                    <span>Show Less</span>
                                    <ChevronUp size={14} className="text-white" />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                    .theme12-merchant-details a {
                        color: #00a87a;
                        font-weight: 600;
                        text-decoration: underline;
                        text-decoration-color: rgba(0, 168, 122, 0.4);
                        transition: all 0.2s ease;
                    }
                    .theme12-merchant-details a:hover {
                        color: #00d69f;
                        text-decoration-color: #00d69f;
                    }
                    .theme12-merchant-details strong,
                    .theme12-merchant-details b {
                        color: #0f172a;
                        font-weight: 700;
                    }
                    .theme12-merchant-details p {
                        margin-bottom: 0.75rem;
                        line-height: 1.65;
                    }
                    .theme12-merchant-details p:last-child {
                        margin-bottom: 0;
                    }
                    .theme12-merchant-details ul {
                        list-style-type: disc;
                        padding-left: 1.25rem;
                        margin-bottom: 0.75rem;
                    }
                    .theme12-merchant-details ol {
                        list-style-type: decimal;
                        padding-left: 1.25rem;
                        margin-bottom: 0.75rem;
                    }
                    .theme12-merchant-details li {
                        margin-bottom: 0.35rem;
                    }
                    .theme12-merchant-details h1,
                    .theme12-merchant-details h2,
                    .theme12-merchant-details h3,
                    .theme12-merchant-details h4 {
                        color: #0f172a;
                        font-weight: 700;
                        margin-top: 1rem;
                        margin-bottom: 0.5rem;
                    }
                `
            }} />
        </div>
    )
}

export default MerchantDetailsShort
